import { useEffect, useMemo, useState } from "react";
import { useDynamicContext, useIsLoggedIn } from "@dynamic-labs/sdk-react-core";

import { BrandLogo } from "./BrandLogo";
import {
	ArrowRightIcon,
	CheckIcon,
	CopyIcon,
	ExternalLinkIcon,
	MoonIcon,
	SunIcon,
	WalletIcon,
} from "./Icons";
import { shortAddr } from "@/lib/utils";

type Theme = "light" | "dark";
type CopyState = "idle" | "copied" | "error";

const SDK_PACKAGE_URL = "https://www.npmjs.com/package/obliq-sdk";
const SDK_VERSION = "0.2.0";

const PAYMASTER_SNIPPET = `// Standalone client
import { createObliqClient } from "obliq-sdk";
const obliq = createObliqClient("http://localhost:8080");
const tokens = await obliq.getSupportedTokens();
const { signature } = await obliq.signAndSendTransaction({
  transaction: "base64EncodedTransaction",
});

// Kit client
import { createKitObliqClient } from "obliq-sdk";
import { address } from "@solana/kit";
const client = await createKitObliqClient({
  endpoint: "http://localhost:8080",
  rpcUrl: "https://api.mainnet-beta.solana.com",
  feeToken: address("EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v"),
  feePayerWallet: userSigner,
});
const blockhash = await client.obliq.getBlockhash();`;

async function copyToClipboard(text: string) {
	if (navigator.clipboard?.writeText) {
		try {
			await navigator.clipboard.writeText(text);
			return;
		} catch {
			// Fall through for browsers that expose Clipboard API but deny it in this context.
		}
	}

	const textarea = document.createElement("textarea");
	textarea.value = text;
	textarea.setAttribute("readonly", "");
	textarea.style.position = "fixed";
	textarea.style.top = "-999px";
	document.body.appendChild(textarea);
	textarea.focus();
	textarea.select();
	textarea.setSelectionRange(0, text.length);

	try {
		if (!document.execCommand("copy")) {
			throw new Error("Copy command failed");
		}
	} finally {
		document.body.removeChild(textarea);
	}
}

interface LandingPageProps {
	theme: Theme;
	onThemeChange: (theme: Theme) => void;
	onOpenDemo: () => void;
}

type FeatureIconName = "accounts" | "lightning" | "shield" | "key";

const FEATURES: Array<{
	title: string;
	copy: string;
	icon: FeatureIconName;
}> = [
	{
		title: "Sponsor account creation & gas fees",
		copy: "Onboard users and cover gas so they can start transacting immediately.",
		icon: "accounts",
	},
	{
		title: "Use your token as native gas",
		copy: "Let users pay fees in the tokens they already hold. No SOL needed.",
		icon: "lightning",
	},
	{
		title: "Users never need SOL",
		copy: "OBLIQ abstracts gas completely so your users never need SOL.",
		icon: "shield",
	},
	{
		title: "Enforce signing of certain transactions",
		copy: "Require additional approvals or policies for high-risk actions.",
		icon: "key",
	},
];

const SOLUTIONS = [
	{
		id: "neobanks",
		label: "Neobanks",
		title: "Seamless Financial Experiences",
		copy: "Enable smooth money movement and account abstraction for modern neobanks.",
		tags: ["Stablecoin Payments", "Fiat Onramps"],
	},
	{
		id: "gaming",
		label: "Gaming",
		title: "Stablecoin Payments",
		copy: "Let users pay with stablecoins they hold, with no SOL friction.",
		tags: ["Game Wallets", "In-app Tokens"],
	},
	{
		id: "defi",
		label: "DeFi Protocols",
		title: "Programmatic Fee Sponsorship",
		copy: "Route deposits, claims, and policy-gated actions through rules that keep gas invisible.",
		tags: ["Deposits", "Claims"],
	},
	{
		id: "nft",
		label: "NFT Marketplaces",
		title: "One-click Marketplace Actions",
		copy: "Reduce listing, bidding, and purchase drop-off with sponsored transaction fees.",
		tags: ["Minting", "Bids"],
	},
] as const;

function LandingThemeToggle({
	theme,
	onThemeChange,
}: {
	theme: Theme;
	onThemeChange: (theme: Theme) => void;
}) {
	return (
		<div className="landing-theme-toggle" role="group" aria-label="Theme">
			<button
				type="button"
				data-active={theme === "light"}
				onClick={() => onThemeChange("light")}
				aria-label="Light theme"
			>
				<SunIcon />
			</button>
			<button
				type="button"
				data-active={theme === "dark"}
				onClick={() => onThemeChange("dark")}
				aria-label="Dark theme"
			>
				<MoonIcon />
			</button>
		</div>
	);
}

function LandingFeatureIcon({ name }: { name: FeatureIconName }) {
	if (name === "lightning") {
		return (
			<svg viewBox="0 0 24 24" aria-hidden="true">
				<path d="M13 2 4 14h7l-1 8 10-13h-7l1-7Z" />
			</svg>
		);
	}

	if (name === "shield") {
		return (
			<svg viewBox="0 0 24 24" aria-hidden="true">
				<path d="M12 3 5 6v5c0 4.8 2.9 8.2 7 10 4.1-1.8 7-5.2 7-10V6l-7-3Z" />
				<path d="m8.8 12 2 2 4.4-5" />
			</svg>
		);
	}

	if (name === "key") {
		return (
			<svg viewBox="0 0 24 24" aria-hidden="true">
				<circle cx="8" cy="15" r="4" />
				<path d="m11 12 8-8 2 2-2 2 2 2-2 2-2-2-3 3" />
			</svg>
		);
	}

	return (
		<svg viewBox="0 0 24 24" aria-hidden="true">
			<circle cx="9" cy="8" r="4" />
			<path d="M3 21v-2a6 6 0 0 1 8.8-5.3" />
			<path d="m14 17 2 2 5-6" />
		</svg>
	);
}

function ProductPreview() {
	const [copyState, setCopyState] = useState<CopyState>("idle");

	useEffect(() => {
		if (copyState === "idle") return;

		const timeout = window.setTimeout(() => setCopyState("idle"), 1800);
		return () => window.clearTimeout(timeout);
	}, [copyState]);

	const handleCopySnippet = async () => {
		try {
			await copyToClipboard(PAYMASTER_SNIPPET);
			setCopyState("copied");
		} catch {
			setCopyState("error");
		}
	};

	return (
		<div className="landing-preview" aria-label="OBLIQ paymaster setup preview">
			<div className="landing-terminal">
				<div className="landing-terminal-row">
					<code>$ npm i obliq-sdk@latest @solana/kit</code>
				</div>
				<div className="landing-terminal-row">
					<code>$ npm i @solana-program/token @solana/kit-plugin-rpc</code>
				</div>
				<div className="landing-terminal-ok">
					<CheckIcon />
					<span>obliq-sdk v{SDK_VERSION} ready</span>
				</div>
			</div>

			<div className="landing-code-card">
				<div className="landing-code-title">
					<div className="landing-code-meta">
						<span className="landing-code-file">client.ts</span>
						<strong>obliq-sdk@{SDK_VERSION}</strong>
					</div>
					<button
						type="button"
						className="landing-copy-button"
						data-state={copyState}
						onClick={() => void handleCopySnippet()}
						aria-label="Copy OBLIQ paymaster code"
						title="Copy code"
					>
						{copyState === "copied" ? <CheckIcon /> : <CopyIcon />}
						<span>
							{copyState === "copied" && "Copied"}
							{copyState === "error" && "Try again"}
							{copyState === "idle" && "Copy"}
						</span>
					</button>
				</div>
				<pre className="landing-code-block">
					<code>{PAYMASTER_SNIPPET}</code>
				</pre>
			</div>
		</div>
	);
}

export function LandingPage({ theme, onThemeChange, onOpenDemo }: LandingPageProps) {
	const [activeSolutionId, setActiveSolutionId] = useState<(typeof SOLUTIONS)[number]["id"]>(
		"neobanks",
	);
	const { primaryWallet, handleLogOut, setShowAuthFlow, sdkHasLoaded } = useDynamicContext();
	const isLoggedIn = useIsLoggedIn();

	const activeSolution = useMemo(
		() => SOLUTIONS.find((solution) => solution.id === activeSolutionId) ?? SOLUTIONS[0],
		[activeSolutionId],
	);

	return (
		<div className="landing-page" id="top">
			<header className="landing-header">
				<a className="landing-brand" href="#top" aria-label="OBLIQ home">
					<BrandLogo theme={theme} />
				</a>


				<div className="landing-header-actions">
					<LandingThemeToggle theme={theme} onThemeChange={onThemeChange} />
					{isLoggedIn && primaryWallet ? (
						<button
							type="button"
							className="landing-login landing-wallet"
							onClick={() => void handleLogOut()}
							aria-label="Disconnect wallet"
						>
							<WalletIcon />
							<span>{shortAddr(primaryWallet.address)}</span>
						</button>
					) : (
						<button
							type="button"
							className="landing-login"
							disabled={!sdkHasLoaded}
							onClick={() => setShowAuthFlow(true)}
						>
							{sdkHasLoaded ? "Login" : "Loading"}
						</button>
					)}
					<button type="button" className="landing-demo-cta" onClick={onOpenDemo}>
						Demo
					</button>
				</div>
			</header>

			<main>
				<section className="landing-hero" id="network">
					<div className="landing-hero-copy">
						<h1>
							<span className="landing-hero-main">
								Frictionless
								<br />
								onboarding.
							</span>
							<span className="landing-hero-accent">
								"Zero SOL"{" "}
								<br className="landing-mobile-break" />
								required.
							</span>
						</h1>
						<p>
							Accept transaction fees in any token. Users transact with tokens they already
							have while OBLIQ handles the plumbing.
						</p>
						<div className="landing-hero-actions">
							<button type="button" className="landing-primary-action" onClick={onOpenDemo}>
								Demo
							</button>
							<a
								className="landing-secondary-action"
								href={SDK_PACKAGE_URL}
								target="_blank"
								rel="noreferrer"
							>
								Documentation
								<ExternalLinkIcon />
							</a>
						</div>
					</div>

					<ProductPreview />
				</section>

				<section className="landing-section landing-features" id="infrastructure">
					<div className="landing-section-head">
						<h2>Everything you need to ship the best payment experience</h2>
					</div>
					<div className="landing-feature-grid">
						{FEATURES.map((feature) => (
							<article className="landing-feature" key={feature.title}>
								<span className="landing-feature-icon">
									<LandingFeatureIcon name={feature.icon} />
								</span>
								<div>
									<h3>{feature.title}</h3>
									<p>{feature.copy}</p>
								</div>
								<ArrowRightIcon className="landing-feature-arrow" />
							</article>
						))}
					</div>
				</section>

				<section className="landing-section landing-solutions" id="docs">
					<div className="landing-section-head">
						<h2>Built for high-performance applications</h2>
					</div>

					<div className="landing-solution-tabs" role="tablist" aria-label="Solutions">
						{SOLUTIONS.map((solution) => (
							<button
								type="button"
								key={solution.id}
								role="tab"
								aria-selected={solution.id === activeSolutionId}
								onClick={() => setActiveSolutionId(solution.id)}
							>
								{solution.label}
							</button>
						))}
					</div>

					<div className="landing-solution-panel" id="pricing">
						<div>
							<h3>{activeSolution.title}</h3>
							<p>{activeSolution.copy}</p>
						</div>
						<div className="landing-solution-tags">
							{activeSolution.tags.map((tag) => (
								<span key={tag}>{tag}</span>
							))}
						</div>
					</div>
				</section>

				<section className="landing-final-cta">
					<h2>Ready to optimize?</h2>
					<div className="landing-final-actions">
						<button type="button" className="landing-primary-action" onClick={onOpenDemo}>
							Demo
						</button>
						<a
							className="landing-secondary-action"
							href={SDK_PACKAGE_URL}
							target="_blank"
							rel="noreferrer"
						>
							Documentation
							<ExternalLinkIcon />
						</a>
					</div>
				</section>
			</main>

			<footer className="landing-footer">
				<BrandLogo theme={theme} />
				<nav aria-label="Footer links">
					<a href={SDK_PACKAGE_URL} target="_blank" rel="noreferrer">
						Docs
					</a>
				</nav>
			</footer>
		</div>
	);
}
